import {useState, useRef, useEffect, useId} from 'react'
import {formatHandicapForDisplay, parseHandicapInput} from '@utils/handicapFormatters.ts'
import styles from './ImpactInput.module.css'

interface ImpactHandicapInputProps {
    value: number | string | null
    onChange: (value: number | null) => void
    onBlur?: () => void
    disabled?: boolean
    variant?: 'light' | 'dark'
    className?: string
    placeholder?: string
}

const ImpactHandicapInput: React.FC<ImpactHandicapInputProps> = ({
    value,
    onChange,
    onBlur,
    disabled = false,
    variant = 'light',
    className = '',
    placeholder
}) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const id = useId()
    const [displayValue, setDisplayValue] = useState(() => formatHandicapForDisplay(value))
    const [isFocused, setIsFocused] = useState(false)
    const [prevValue, setPrevValue] = useState(value)

    // Prevent browser autofill by making field readonly initially, then removing after delay
    useEffect(() => {
        const timer = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.removeAttribute('readonly')
                // Force clear any autofilled value
                if (inputRef.current.value !== displayValue) {
                    inputRef.current.value = displayValue
                }
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [displayValue])

    if (value !== prevValue && !isFocused) {
        setPrevValue(value)
        setDisplayValue(formatHandicapForDisplay(value))
    }

    const handleFocus = () => {
        setIsFocused(true)
        // Extra safety: remove readonly on focus
        if (inputRef.current) {
            inputRef.current.removeAttribute('readonly')
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newDisplayValue = e.target.value

        if (newDisplayValue.startsWith('-')) {
            newDisplayValue = '+' + newDisplayValue.substring(1)
        }

        setDisplayValue(newDisplayValue)

        const parsed = parseHandicapInput(newDisplayValue)
        onChange(parsed)
    }

    const handleBlur = () => {
        setIsFocused(false)
        const parsed = parseHandicapInput(displayValue)
        if (parsed !== null) {
            setDisplayValue(formatHandicapForDisplay(parsed))
        }

        if (onBlur) {
            onBlur()
        }
    }

    const variantClass = variant === 'dark' ? styles.inputDark : styles.inputLight

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            className={`${variantClass} ${className}`}
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            name="handicap-index-field"
            id={`handicap-input${id}`}
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            readOnly
        />
    )
}

export default ImpactHandicapInput
